<script setup lang="ts">
import { defineAsyncComponent, onBeforeUnmount, onMounted, ref } from 'vue'
import { Languages, LogOut, Settings } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import Tools from '@/utils/tools'
import { DynamicModalType, type IUserUpdateAPI, PromptType, Target } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import { UsernameError } from '@/errors/FapFapError'
import { useDynamicModalStore } from '@/stores/dynamicModalStore'
import { useSearchStore } from '@/stores/searchStoreV2'
import RedeemG2A from '@/components/subscription/RedeemG2A.vue'
import Footer from '@/components/Footer.vue'

const EditableInput = defineAsyncComponent(() => import('@/components/inputs/EditableInput.vue'))
const Username = defineAsyncComponent(() => import('@/components/user/settings/Username.vue'))
const AudioControl = defineAsyncComponent(() => import('@/components/user/settings/AudioControl.vue'))
const PromptChips = defineAsyncComponent(() => import('@/components/search/PromptChips.vue'))
const RedeemReferral = defineAsyncComponent(() => import('@/components/user/settings/RedeemReferral.vue'))
const ShareReferral = defineAsyncComponent(() => import('@/components/user/settings/ShareReferral.vue'))
const RenewPremium = defineAsyncComponent(() => import('@/components/subscription/RenewPremium.vue'))

const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const keepUpdated = ref<boolean>(false)

const previousMode = ref<Target>(Target.Gallery)

onMounted(async () => {
	keepUpdated.value = user.value?.keep_updated
	previousMode.value = await useSearchStore().loadBlackListedTags()
})

onBeforeUnmount(() => {
	useSearchStore().mode = previousMode.value
})

const passwordErrorHandlers = [
	{
		condition: (password: string) => password.length < 8 || password.length > 32,
		message: t('register.invalid_password'),
	},
]

async function updatePassword(pwd: string): Promise<string> {
	const data: Partial<IUserUpdateAPI> = { pwd }

	try {
		await authStore.updateUser(data)
	}
	catch (error: any) {
		if (error instanceof UsernameError) {
			return t(`register.${error.code}`)
		}
	}
	return ''
}

const updateKeepUpdated = Tools.createDebouncedFunction(async () => {
	if (keepUpdated.value === user.value.keep_updated) {
		return
	}
	const data: Partial<IUserUpdateAPI> = {
		user: {
			keep_updated: keepUpdated.value,
		},
	}

	try {
		await authStore.updateUser(data)
	}
	catch (error: any) {
		if (error instanceof UsernameError) {
			return t(`register.${error.code}`)
		}
	}
}, 300)

async function logout() {
	await authStore.logout()
	useDynamicModalStore().closeModal()
	router.push({
		path: '/explore',
		query: route.query,
	})
}
</script>

<template>
	<div class="w-full gap-[20px] menu:mt-[110px] mt-[160px] mx-auto flex flex-col p-4 md:p-0 md:w-[460px] min-h-[calc(100dvh-70px)]">
		<h1 class="font-extrabold w-fit text-2xl menu:text-3xl mx-auto">
			{{ t('common.settings') }}
		</h1>
		<div class="setting-card">
			<div class="setting-card-header">
				<span class="flex-center gap-2">
					<Settings class="lucide-button-icon-lg" />

				</span>
			</div>
			<div class="setting-card-content !gap-4">
				<Username />
				<EditableInput
					:label="t('dashboard.password_label')"
					type="password"
					:placeholder="t('dashboard.password')"
					:error-handlers="passwordErrorHandlers"
					:max-length="32"
					label-value="********"
					:on-save="updatePassword"
					class="mt-1"
				/>

				<AudioControl />
				<div class="flex justify-between">
					<label
						for="keepupdated"
						class="cursor-pointer select-none text-sm font-semibold"
					>
						{{ t('register.keep_updated') }}
					</label>
					<Checkbox
						v-model="keepUpdated"
						input-id="keepupdated"
						name="keepupdated"
						:binary="true"
						@change="updateKeepUpdated"
					/>
				</div>
			</div>
		</div>

		<RenewPremium />
		<RedeemG2A />
		<ShareReferral />
		<RedeemReferral />

		<PromptChips :type="PromptType.Blacklist" />

		<Button
			class="button-red-shade my-10"
			outlined
			@click="logout"
		>
			<template #default>
				<LogOut class="lucide-button-icon" />
				<span>{{ t('menu.logout') }}</span>
			</template>
		</Button>
	</div>
	<Footer />
</template>

<style scoped lang="scss">

</style>
